const productImageBase = '/assets/images/products';

const imagePath = (categoryFolder, fileName) => `${productImageBase}/${categoryFolder}/${fileName}`;

export const productCategories = [
  {
    slug: 'liquid-feed-supplements',
    name: 'Liquid Feed Supplements',
    folder: 'public/assets/images/products/liquid-feed-supplements',
    description: 'Liquid nutritional supplements for daily livestock support, pregnancy care, udder health, and production performance.'
  },
  {
    slug: 'mineral-feed-supplements',
    name: 'Mineral Feed Supplements',
    folder: 'public/assets/images/products/mineral-feed-supplements',
    description: 'Mineral and vitamin blends for bioavailability, metabolism, fertility, growth, and production support.'
  },
  {
    slug: 'reproductive-care',
    name: 'Reproductive Care',
    folder: 'public/assets/images/products/reproductive-care',
    description: 'Reproductive support formulations for heat, conception, uterine wellness, and post-calving programs.'
  },
  {
    slug: 'calf-growth-nutrition',
    name: 'Calf Growth Nutrition',
    folder: 'public/assets/images/products/calf-growth-nutrition',
    description: 'Calf nutrition support products for young animal growth, vitality, immunity, and development.'
  },
  {
    slug: 'liver-support',
    name: 'Liver Support',
    folder: 'public/assets/images/products/liver-support',
    description: 'Liver and digestion-oriented supplementation for feed utilization, appetite, and metabolic support.'
  },
  {
    slug: 'energy-immunity',
    name: 'Energy & Immunity Support',
    folder: 'public/assets/images/products/energy-immunity',
    description: 'Performance supplements for energy balance, immunity, stress recovery, and overall livestock wellness.'
  }
];

export const products = [
  {
    slug: 'lacto-zyv-ad3-gold',
    name: 'Lacto ZYV AD3 Gold',
    category: 'Liquid Feed Supplements',
    categorySlug: 'liquid-feed-supplements',
    description: 'Liquid feed supplement with calcium, phosphorus, vitamins, and herbal support for milk production, immunity, skeletal strength, and reproductive wellness.',
    benefits: ['Supports milk production and fat level', 'Helps prevent milk fever, rickets, and haematuria', 'Supports skeletal and muscular strength', 'Improves disease resistance and reproductive wellness'],
    ingredients: ['Calcium', 'Phosphorus', 'Vitamin A', 'Vitamin D3', 'Vitamin E', 'Vitamin B12', 'Leptadenia reticulate extract', 'Jatamansi', 'Shatavari'],
    usage: 'Use as directed on the product label or by a qualified veterinarian/nutrition advisor for the animal species and production stage.',
    packaging: ['1 ltr', '2 ltr', '5 ltr', '10 ltr', '20 ltr', '40 ltr'],
    image: imagePath('liquid-feed-supplements', 'lacto-zyv-ad3-gold.jpg')
  },
  {
    slug: 'prega-zyv-liquid',
    name: 'Prega ZYV Liquid',
    category: 'Liquid Feed Supplements',
    categorySlug: 'liquid-feed-supplements',
    description: 'Conception support liquid fortified with multivitamins, trace minerals, amino acids, and conception enhancer herbs.',
    benefits: ['Supports conception programs', 'Provides omega 3, omega 6, and omega 9 fatty acid support', 'Helps reproductive performance after A.I.', 'Practical 200 ml field presentation'],
    ingredients: ['Multivitamins', 'Trace minerals', 'Amino acids', 'Conception enhancer herbs extract', 'Omega fatty acid support'],
    usage: '200 ml soon after A.I. or as advised by a qualified veterinarian.',
    packaging: ['200 ml bottle'],
    image: imagePath('liquid-feed-supplements', 'prega-zyv-liquid.jpg')
  },
  {
    slug: 'lacto-zyv-vita-h',
    name: 'Lacto ZYV Vita-H',
    category: 'Liquid Feed Supplements',
    categorySlug: 'liquid-feed-supplements',
    description: 'Vitamin-rich liquid supplement formulated to support udder health, immunity, reproduction, and production performance.',
    benefits: ['Improves udder health and milk production', 'Supports milk holding capacity', 'Supports fertility and conception rate', 'Supports young animal growth and disease recovery'],
    ingredients: ['Vitamin A', 'Vitamin D3', 'Vitamin B12', 'Vitamin E', 'Selenium', 'Vitamin H (Biotin)'],
    usage: 'Follow label dosage by species. Use under veterinary guidance for disease recovery or special production programs.',
    packaging: ['250 ml bottle', '500 ml bottle', '1 ltr bottle'],
    image: imagePath('liquid-feed-supplements', 'lacto-zyv-vita-h.jpg')
  },
  {
    slug: 'zyv-min-forte',
    name: 'ZYV Min Forte',
    category: 'Mineral Feed Supplements',
    categorySlug: 'mineral-feed-supplements',
    description: 'Chelated minerals and vitamins blend for assured bioavailability, milk production support, fertility, immunity, and growth.',
    benefits: ['Maximum bioavailability and mineral utilization', 'Improves milk production and SNF value', 'Supports fertility and conception rate', 'Supports immunity, health, and growth'],
    ingredients: ['Calcium', 'Phosphorus', 'Cobalt', 'Copper', 'Iron', 'Manganese', 'Zinc', 'Chromium', 'Selenium', 'Vitamins A, D3, B3, E', 'Biotin', 'Live yeast culture'],
    usage: 'Mix with feed as per label directions for large animals, small animals, and cattle feed inclusion rates.',
    packaging: ['1 kg', '5 kg', '10 kg', '20 kg'],
    image: imagePath('mineral-feed-supplements', 'zyv-min-forte.jpg')
  },
  {
    slug: 'meta-zyv-plus-powder',
    name: 'Meta ZYV Plus Powder',
    category: 'Mineral Feed Supplements',
    categorySlug: 'mineral-feed-supplements',
    description: 'Anionic calcium-phosphorus metabolism support powder for transition animals, smooth calving, and udder development support.',
    benefits: ['Helps mobilise calcium and phosphorus', 'Helps reduce chances of hypocalcemia', 'Supports smooth calving', 'Supports udder development and reduces chances of ROP'],
    ingredients: ['Anionic blend', 'Calcium support', 'Phosphorus support', 'Vitamin-H support', 'Metabolism support nutrients'],
    usage: 'Feed regularly as directed on label during the recommended transition or pregnancy support window.',
    packaging: ['20 pouches of 50 gm each'],
    image: imagePath('mineral-feed-supplements', 'meta-zyv-plus-powder.jpg')
  },
  {
    slug: 'repromix-bolus',
    name: 'ReproMix Bolus',
    category: 'Reproductive Care',
    categorySlug: 'reproductive-care',
    description: 'Reproductive bolus formulated with herbs, chelated minerals, vitamins, and trace elements to support heat and breeding outcomes.',
    benefits: ['Supports infertility and anoestrus programs', 'Supports silent heat and delayed ovulation programs', 'Supports repeat breeding management', 'Helps reproductive hormone synthesis'],
    ingredients: ['Poly herbs', 'Chelated zinc', 'Chelated manganese', 'Cobalt', 'Copper', 'Iron', 'Iodine', 'Selenium', 'Bioactive chromium', 'Vitamins A, E, D3, K', 'Phosphorus'],
    usage: 'To improve heat: 1 bolus for 10 days during the cycle, or as advised by a qualified veterinarian.',
    packaging: ['1 x 10 bolus'],
    image: imagePath('reproductive-care', 'repromix-bolus.jpg')
  },
  {
    slug: 'zyv-power-calf',
    name: 'ZYV Power Calf',
    category: 'Calf Growth Nutrition',
    categorySlug: 'calf-growth-nutrition',
    description: 'Young animal nutrition support for calf vitality, growth, immunity, and development during early life stages.',
    benefits: ['Supports calf growth and vitality', 'Helps immunity development', 'Supports better feed response', 'Practical calf nutrition support'],
    ingredients: ['Vitamins', 'Minerals', 'Growth support nutrients', 'Immunity support nutrients'],
    usage: 'Use as per label directions for calves and young animals, or as recommended by a veterinarian/nutrition advisor.',
    packaging: ['Upload label pack sizes'],
    image: imagePath('calf-growth-nutrition', 'zyv-power-calf.jpg')
  },
  {
    slug: 'liv-o-zyv-forte',
    name: 'LIV-O-ZYV Forte',
    category: 'Liver Support',
    categorySlug: 'liver-support',
    description: 'Liver support supplement for appetite, digestion, feed utilization, and metabolic wellness in livestock.',
    benefits: ['Supports liver function', 'Helps appetite and digestion', 'Supports feed utilization', 'Supports metabolic wellness'],
    ingredients: ['Liver support herbs', 'Digestive support nutrients', 'Vitamins', 'Minerals'],
    usage: 'Use as directed on the product label or by a qualified veterinarian for the animal condition and production stage.',
    packaging: ['Upload label pack sizes'],
    image: imagePath('liver-support', 'liv-o-zyv-forte.jpg')
  },
  {
    slug: 'zyv-boost-plus',
    name: 'ZYV BOOST+',
    category: 'Energy & Immunity Support',
    categorySlug: 'energy-immunity',
    description: 'Performance support supplement for energy balance, immunity, stress recovery, and overall livestock wellness.',
    benefits: ['Supports energy and vitality', 'Helps immune response', 'Supports recovery from stress', 'Improves everyday performance support'],
    ingredients: ['Energy support nutrients', 'Vitamins', 'Minerals', 'Immunity support nutrients'],
    usage: 'Use as directed on product label or as advised by a qualified veterinarian/nutrition advisor.',
    packaging: ['Upload label pack sizes'],
    image: imagePath('energy-immunity', 'zyv-boost-plus.jpg')
  },
  {
    slug: 'utero-zyv-tonic',
    name: 'Utero ZYV Tonic',
    category: 'Reproductive Care',
    categorySlug: 'reproductive-care',
    description: 'Uterine and post-calving reproductive wellness tonic for breeding readiness, recovery, and reproductive performance support.',
    benefits: ['Supports uterine wellness', 'Helps post-calving recovery programs', 'Supports breeding readiness', 'Practical reproductive care support'],
    ingredients: ['Uterine wellness herbs', 'Vitamins', 'Minerals', 'Reproductive support nutrients'],
    usage: 'Use as directed on label for reproductive care programs or as advised by a qualified veterinarian.',
    packaging: ['Upload label pack sizes'],
    image: imagePath('reproductive-care', 'utero-zyv-tonic.jpg')
  }
];
